// Cliente del Worker de transcripción — ver
// openspec/changes/video-multi-audio-tracks design.md §5. Decodifica el
// archivo de audio/video a PCM mono 16kHz (lo que espera Whisper) con la Web
// Audio API, y despacha la inferencia al Worker para no bloquear la UI.
//
// Gap conocido y aceptado (ver tasks.md §4.4): si `decodeAudioData` no puede
// decodificar el contenedor del archivo (pasa con algunos .mov/.mkv en
// ciertos navegadores), este archivo NO intenta un fallback con ffmpeg.wasm
// — eso habría significado una segunda dependencia WASM pesada sin poder
// verificar que compila en este entorno (ver conversación de implementación).
// El track simplemente queda sin transcripción, lo cual el spec ya contempla
// como comportamiento válido ("falla o ausencia de transcripción no bloquea
// el video").
import type { TranscriptionRequest, TranscriptionResponse } from "../workers/transcriptionWorker"

const TARGET_SAMPLE_RATE = 16000

export async function decodeAudioToMono16k(file: Blob): Promise<Float32Array> {
  const arrayBuffer = await file.arrayBuffer()
  // Un AudioContext "normal" no permite fijar el sampleRate de decodeAudioData
  // en todos los navegadores — se decodifica a la tasa nativa del archivo y
  // se resamplea a 16kHz con un OfflineAudioContext, que sí lo permite.
  const probeContext = new AudioContext()
  let decoded: AudioBuffer
  try {
    decoded = await probeContext.decodeAudioData(arrayBuffer.slice(0))
  } finally {
    await probeContext.close()
  }

  const durationSeconds = decoded.duration
  const offline = new OfflineAudioContext(1, Math.ceil(durationSeconds * TARGET_SAMPLE_RATE), TARGET_SAMPLE_RATE)
  const source = offline.createBufferSource()
  source.buffer = decoded
  const merger = offline.createChannelMerger(1)
  source.connect(merger)
  merger.connect(offline.destination)
  source.start()
  const resampled = await offline.startRendering()
  return resampled.getChannelData(0).slice()
}

let workerSingleton: Worker | null = null
const pending = new Map<string, { resolve: (vtt: string) => void; reject: (err: Error) => void }>()
const progressCallbacks = new Map<string, (status: string, progress?: number) => void>()

function getWorker(): Worker {
  if (!workerSingleton) {
    workerSingleton = new Worker(new URL("../workers/transcriptionWorker.ts", import.meta.url), { type: "module" })
    workerSingleton.onmessage = (event: MessageEvent<TranscriptionResponse>) => {
      const msg = event.data
      if (msg.type === "progress") {
        progressCallbacks.get(msg.requestId)?.(msg.status, msg.progress)
        return
      }
      const entry = pending.get(msg.requestId)
      pending.delete(msg.requestId)
      progressCallbacks.delete(msg.requestId)
      if (!entry) return
      if (msg.type === "result") entry.resolve(msg.vtt)
      else entry.reject(new Error(msg.message))
    }
  }
  return workerSingleton
}

export async function transcribeAudioFile(
  file: Blob,
  onProgress?: (status: string, progress?: number) => void,
): Promise<string> {
  const samples = await decodeAudioToMono16k(file)
  const worker = getWorker()
  const requestId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

  if (onProgress) {
    progressCallbacks.set(requestId, onProgress)
  }

  return new Promise<string>((resolve, reject) => {
    pending.set(requestId, { resolve, reject })
    const message: TranscriptionRequest = { type: "transcribe", requestId, samples }
    // El buffer subyacente de `samples` se transfiere (no se copia) al
    // Worker — no lo reuses después de llamar a esta función.
    worker.postMessage(message, [samples.buffer])
  })
}
